import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Header from './Header';
import './Events.css';

const formatMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

function Events() {
  const { venueSlug = '' } = useParams();
  const [searchParams] = useSearchParams();
  const queryVenueId = searchParams.get('venueId') || '';
  const API_HOST = useMemo(() => (
    process.env.REACT_APP_VENUE_API_HOST ||
    process.env.REACT_APP_API_HOST ||
    ''
  ), []);

  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [siteInventory, setSiteInventory] = useState({ camping_sites: [], vendor_sites: [], registration_open: false });
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingEventDetail, setLoadingEventDetail] = useState(false);
  const [resolvingVenue, setResolvingVenue] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resolvedVenueId, setResolvedVenueId] = useState('');
  const [resolvedVenueName, setResolvedVenueName] = useState('');
  const [venueStaff, setVenueStaff] = useState([]);

  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    price_tier_id: '',
    camping_site_ids: [],
    vendor_site_ids: []
  });

  const venueId = venueSlug ? resolvedVenueId : queryVenueId;

  useEffect(() => {
    let cancelled = false;

    async function resolveVenueBySlug() {
      if (!venueSlug || !API_HOST) {
        setResolvedVenueId('');
        setResolvedVenueName('');
        return;
      }

      setResolvingVenue(true);
      setError('');
      try {
        const res = await fetch(`${API_HOST}/api/venues/slug/${encodeURIComponent(venueSlug)}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Unable to resolve venue URL.');

        if (!cancelled) {
          setResolvedVenueId(String(data._id || ''));
          setResolvedVenueName(data.name || '');
        }
      } catch (err) {
        if (!cancelled) {
          setResolvedVenueId('');
          setResolvedVenueName('');
          setError(err.message || 'Unable to resolve venue URL.');
        }
      } finally {
        if (!cancelled) setResolvingVenue(false);
      }
    }

    resolveVenueBySlug();
    return () => { cancelled = true; };
  }, [venueSlug, API_HOST]);

  useEffect(() => {
    let cancelled = false;
    async function loadEvents() {
      if (!venueId || !API_HOST) return;
      setLoadingEvents(true);
      setError('');
      try {
        const res = await fetch(`${API_HOST}/api/events/public/venue/${venueId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load events.');
        if (!cancelled) {
          setEvents(Array.isArray(data) ? data : []);
          if (Array.isArray(data) && data.length > 0) {
            setSelectedEventId(String(data[0]._id));
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load events.');
      } finally {
        if (!cancelled) setLoadingEvents(false);
      }
    }
    loadEvents();
    return () => { cancelled = true; };
  }, [venueId, API_HOST]);

  useEffect(() => {
    let cancelled = false;
    async function loadVenueStaff() {
      if (!venueId || !API_HOST) return;
      try {
        const res = await fetch(`${API_HOST}/api/venues/${venueId}/staff`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || 'Failed to load venue staff.');
        if (!cancelled) {
          setVenueStaff(Array.isArray(data?.staff) ? data.staff : []);
        }
      } catch {
        if (!cancelled) setVenueStaff([]);
      }
    }
    loadVenueStaff();
    return () => { cancelled = true; };
  }, [venueId, API_HOST]);

  useEffect(() => {
    let cancelled = false;
    async function loadEventDetail() {
      if (!selectedEventId || !API_HOST) return;
      setLoadingEventDetail(true);
      setError('');
      setSuccess('');
      try {
        const [eventRes, sitesRes] = await Promise.all([
          fetch(`${API_HOST}/api/events/public/${selectedEventId}`),
          fetch(`${API_HOST}/api/events/${selectedEventId}/site-inventory`)
        ]);

        const eventData = await eventRes.json();
        const sitesData = await sitesRes.json();

        if (!eventRes.ok) throw new Error(eventData?.error || 'Failed to load event detail.');
        if (!sitesRes.ok) throw new Error(sitesData?.error || 'Failed to load site inventory.');

        if (!cancelled) {
          setSelectedEvent(eventData);
          setSiteInventory(sitesData);
          setForm((prev) => ({
            ...prev,
            price_tier_id: eventData?.price_tiers?.[0]?._id || '',
            camping_site_ids: [],
            vendor_site_ids: []
          }));
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load event details.');
      } finally {
        if (!cancelled) setLoadingEventDetail(false);
      }
    }
    loadEventDetail();
    return () => { cancelled = true; };
  }, [selectedEventId, API_HOST]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const toggleSite = (key, siteId) => {
    setForm((prev) => {
      const exists = prev[key].includes(siteId);
      return {
        ...prev,
        [key]: exists ? prev[key].filter((id) => id !== siteId) : [...prev[key], siteId]
      };
    });
  };

  const selectedTier = selectedEvent?.price_tiers?.find((t) => String(t._id) === String(form.price_tier_id));
  const basePrice = selectedTier?.price_cents || 0;

  const selectedCampTotal = (siteInventory.camping_sites || [])
    .filter((s) => form.camping_site_ids.includes(String(s._id)))
    .reduce((sum, s) => sum + (s.price_cents || 0), 0);

  const selectedVendorTotal = (siteInventory.vendor_sites || [])
    .filter((s) => form.vendor_site_ids.includes(String(s._id)))
    .reduce((sum, s) => sum + (s.price_cents || 0), 0);

  const projectedTotal = basePrice + selectedCampTotal + selectedVendorTotal;

  const submitRegistration = async (e) => {
    e.preventDefault();
    if (!selectedEventId) return;
    if (!form.name.trim() || !form.phone.trim()) {
      setError('Name and phone are required.');
      return;
    }

    setRegistering(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
        price_tier_id: form.price_tier_id || undefined,
        camping_site_ids: form.camping_site_ids,
        vendor_site_ids: form.vendor_site_ids
      };

      const res = await fetch(`${API_HOST}/api/events/${selectedEventId}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Registration failed.');

      setSuccess(`Registration confirmed: ${data?.registration?.registration_number || 'Success'}`);
      setForm((prev) => ({ ...prev, camping_site_ids: [], vendor_site_ids: [] }));

      const sitesRes = await fetch(`${API_HOST}/api/events/${selectedEventId}/site-inventory`);
      const sitesData = await sitesRes.json();
      if (sitesRes.ok) setSiteInventory(sitesData);
    } catch (err) {
      setError(err.message || 'Registration failed.');
    } finally {
      setRegistering(false);
    }
  };

  if (!venueId) {
    return (
      <div className="events-page">
        <Header />
        <div className="events-content">
          <h1>Events</h1>
          {resolvingVenue ? (
            <p>Resolving venue...</p>
          ) : (
            <p className="events-hint">Missing venue context. Open this page using /v/YOUR_VENUE_SLUG/events or /events?venueId=YOUR_VENUE_ID.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="events-page">
      <Header />
      <div className="events-content events-grid">
        <section className="events-list-panel">
          <h1>{resolvedVenueName ? `${resolvedVenueName} Events` : 'Upcoming Events'}</h1>
          {loadingEvents && <p>Loading events...</p>}
          {!loadingEvents && events.length === 0 && <p>No public events found for this venue yet.</p>}
          <div className="events-list">
            {events.map((event) => (
              <button
                key={event._id}
                type="button"
                className={`event-list-item ${String(selectedEventId) === String(event._id) ? 'active' : ''}`}
                onClick={() => setSelectedEventId(String(event._id))}
              >
                <div className="event-list-top">
                  <strong>{event.name}</strong>
                  <span className={`event-pill ${event.registration_open ? 'open' : 'closed'}`}>
                    {event.registration_open ? 'Registration Open' : 'Registration Closed'}
                  </span>
                </div>
                <div className="event-list-meta">
                  <span>{new Date(event.starts_at).toLocaleString()}</span>
                  <span>From {formatMoney(event.min_price_cents || 0)}</span>
                  {!!event?.campsite_counts?.total && <span>Camp {event.campsite_counts.available}/{event.campsite_counts.total}</span>}
                  {!!event?.vendor_site_counts?.total && <span>Vendor {event.vendor_site_counts.available}/{event.vendor_site_counts.total}</span>}
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="events-register-panel">
          {loadingEventDetail && <p>Loading event details...</p>}
          {!loadingEventDetail && selectedEvent && (
            <>
              <h2>{selectedEvent.name}</h2>
              {!!selectedEvent.description && <p className="event-description">{selectedEvent.description}</p>}

              {!!venueStaff.length && (
                <div className="site-group">
                  <h3>Venue Staff</h3>
                  <div className="site-list">
                    {venueStaff.map((member) => (
                      <div key={member.employee_id} className="site-item" style={{ alignItems: 'center' }}>
                        {member.profile_pic_url ? (
                          <img
                            src={member.profile_pic_url}
                            alt={member.name}
                            style={{ width: '44px', height: '44px', borderRadius: '999px', objectFit: 'cover', marginRight: '0.75rem' }}
                          />
                        ) : (
                          <div style={{ width: '44px', height: '44px', borderRadius: '999px', background: '#e5e7eb', marginRight: '0.75rem' }} />
                        )}
                        <span>{member.name}</span>
                        <span style={{ marginLeft: 'auto' }}>{member.role}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form className="register-form" onSubmit={submitRegistration}>
                <div className="form-row">
                  <label>Name *</label>
                  <input value={form.name} onChange={(e) => setField('name', e.target.value)} required />
                </div>
                <div className="form-row">
                  <label>Phone *</label>
                  <input value={form.phone} onChange={(e) => setField('phone', e.target.value)} required />
                </div>
                <div className="form-row">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                </div>

                {!!selectedEvent.price_tiers?.length && (
                  <div className="form-row">
                    <label>Ticket Tier</label>
                    <select value={form.price_tier_id} onChange={(e) => setField('price_tier_id', e.target.value)}>
                      {selectedEvent.price_tiers.map((tier) => (
                        <option key={tier._id} value={tier._id}>{tier.name} ({formatMoney(tier.price_cents)})</option>
                      ))}
                    </select>
                  </div>
                )}

                {!!siteInventory?.camping_sites?.length && (
                  <div className="site-group">
                    <h3>Camping Sites</h3>
                    <div className="site-list">
                      {siteInventory.camping_sites.map((site) => {
                        const available = site.status === 'available';
                        const checked = form.camping_site_ids.includes(String(site._id));
                        return (
                          <label key={site._id} className={`site-item ${available ? '' : 'disabled'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!available}
                              onChange={() => toggleSite('camping_site_ids', String(site._id))}
                            />
                            <span>{site.code} {site.name ? `- ${site.name}` : ''}</span>
                            <span>{site.billing_mode === 'sold' ? formatMoney(site.price_cents) : 'Free'}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {!!siteInventory?.vendor_sites?.length && (
                  <div className="site-group">
                    <h3>Vendor Sites</h3>
                    <div className="site-list">
                      {siteInventory.vendor_sites.map((site) => {
                        const available = site.status === 'available';
                        const checked = form.vendor_site_ids.includes(String(site._id));
                        return (
                          <label key={site._id} className={`site-item ${available ? '' : 'disabled'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={!available}
                              onChange={() => toggleSite('vendor_site_ids', String(site._id))}
                            />
                            <span>{site.code} {site.name ? `- ${site.name}` : ''}</span>
                            <span>{site.billing_mode === 'sold' ? formatMoney(site.price_cents) : 'Free'}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="totals-row">
                  <span>Projected Total</span>
                  <strong>{formatMoney(projectedTotal)}</strong>
                </div>

                {error && <p className="form-error">{error}</p>}
                {success && <p className="form-success">{success}</p>}

                <button
                  type="submit"
                  className="events-login-button"
                  disabled={registering || !selectedEvent.registration_open || !siteInventory.registration_open}
                >
                  {registering ? 'Submitting...' : (selectedEvent.registration_open && siteInventory.registration_open ? 'Register Now' : 'Registration Closed')}
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}

export default Events;
